<div class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40" (click)="editorClose.emit()">
  <div
    class="flex h-[85vh] w-[90vw] max-w-[1400px] flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
    (click)="$event.stopPropagation()"
  >
    <!-- Toolbar -->
    <div class="flex h-12 shrink-0 items-center justify-between border-b border-neutral-200 bg-neutral-50 px-5">
      <div class="flex items-center gap-1.5">
        <dojo-button
          [size]="'s'"
          [fill]="mode() === 'create-anchor' ? 'solid' : 'outline'"
          (click)="startCreateAnchor()"
        >
          <dojo-icon icon="plus" [size]="12" class="mr-1" />
          Anchor
        </dojo-button>
        <dojo-button
          [size]="'s'"
          [fill]="mode() === 'select-anchor' ? 'solid' : 'outline'"
          (click)="startSelectAnchors()"
        >
          Select
        </dojo-button>
        <dojo-button
          [size]="'s'"
          [fill]="mode() === 'connect-from' || mode() === 'connect-to' ? 'solid' : 'outline'"
          (click)="startCreateConnection()"
        >
          Connect
        </dojo-button>
        @if (mode() !== 'view') {
          <div class="mx-1 h-4 w-px bg-neutral-200"></div>
          <dojo-button [size]="'s'" [fill]="'outline'" (click)="cancelMode()">Cancel</dojo-button>
        }
      </div>

      <div class="flex items-center gap-1.5">
        @if (selectedAnchors().length > 0) {
          <dojo-button [size]="'s'" [fill]="'outline'" (click)="copyAnchorsToClipboard()">
            Copy {{ selectedAnchors().length }} Anchor(s)
          </dojo-button>
        }
        @if (connections().length > 0) {
          <dojo-button [size]="'s'" [fill]="'outline'" (click)="copyConnectionsToClipboard()">
            Copy Connections
          </dojo-button>
        }
        <button
          class="ml-1 flex h-6 w-6 items-center justify-center rounded text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600"
          (click)="editorClose.emit()"
        >
          <dojo-icon icon="xmark" [size]="14" />
        </button>
      </div>
    </div>

    <!-- Mode indicator -->
    @if (mode() !== 'view') {
      <div
        class="flex items-center gap-2 border-b border-amber-100 bg-amber-50/70 px-3 py-1 text-[11px] text-amber-700"
      >
        <dojo-icon icon="info-circle" [size]="12" />
        <span>{{ getModeLabel() }}</span>
        @if (mode() === 'connect-to' && connectFromAnchor()) {
          <span class="rounded bg-blue-500 px-1.5 py-px text-[10px] font-medium text-white">
            From: {{ connectFromAnchor() }}
          </span>
        }
      </div>
    }

    <!-- Main content area -->
    <div class="flex flex-1 overflow-hidden">
      <!-- LaTeX Preview Panel -->
      <div
        class="relative flex-1 overflow-auto p-8"
        #latexContainer
        [class.cursor-crosshair]="mode() !== 'view'"
        [class.select-none]="mode() !== 'view'"
        (mousedown)="onLatexMouseDown($event)"
        (mousemove)="onLatexMouseMove($event); onLatexMouseMoveLasso($event)"
        (mouseup)="onLatexMouseUp($event)"
        (mouseleave)="onLatexMouseUp($event)"
        (click)="onLatexClick($event)"
        (wheel)="onLatexWheel($event)"
      >
        @for (node of content().latex_nodes; track node.id ?? node.latex) {
          <div class="latex-line mb-8">
            <markdown [data]="node.latex" katex [katexOptions]="katexOptions" />
          </div>
        }

        <!-- Hover highlight overlay for existing anchors -->
        @if (hoveredAnchor(); as hovered) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-blue-500 bg-blue-500/20"
            [style.left.px]="getRelativeLeft(hovered.rect)"
            [style.top.px]="getRelativeTop(hovered.rect)"
            [style.width.px]="hovered.rect.width"
            [style.height.px]="hovered.rect.height"
          >
            <span
              class="absolute -top-6 left-0 whitespace-nowrap rounded bg-blue-500 px-1.5 py-0.5 text-[10px] text-white"
            >
              {{ hovered.id }}
            </span>
          </div>
        }

        <!-- Hover highlight for create-anchor mode (non-anchor elements) -->
        @if (mode() === 'create-anchor' && hoveredElement(); as hovered) {
          @if (!hovered.isExistingAnchor) {
            <div
              class="pointer-events-none absolute z-10 rounded border-2 border-dashed border-emerald-500 bg-emerald-500/20"
              [style.left.px]="getRelativeLeft(hovered.rect)"
              [style.top.px]="getRelativeTop(hovered.rect)"
              [style.width.px]="hovered.rect.width"
              [style.height.px]="hovered.rect.height"
            >
              <span
                class="absolute -top-6 left-0 flex items-center gap-1.5 whitespace-nowrap rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] text-white"
              >
                "{{ hovered.text }}"
                @if (hoverableElements().length > 1) {
                  <span class="opacity-75">(scroll: {{ hoverLevel() + 1 }}/{{ hoverableElements().length }})</span>
                }
              </span>
            </div>
          }
        }

        <!-- Selected anchors overlay -->
        @for (anchorId of selectedAnchors(); track anchorId) {
          @if (getAnchorRect(anchorId); as rect) {
            <div
              class="pointer-events-none absolute z-10 rounded border-2 border-emerald-500 bg-emerald-500/20"
              [style.left.px]="getRelativeLeft(rect)"
              [style.top.px]="getRelativeTop(rect)"
              [style.width.px]="rect.width"
              [style.height.px]="rect.height"
            ></div>
          }
        }

        <!-- Lasso selection rectangle -->
        @if (getLassoRect(); as rect) {
          <div
            class="pointer-events-none absolute z-20 border-2 border-dashed border-violet-500 bg-violet-500/10"
            [style.left.px]="rect.left"
            [style.top.px]="rect.top"
            [style.width.px]="rect.width"
            [style.height.px]="rect.height"
          ></div>
        }

        <!-- Lasso selection highlight -->
        @if (lassoSelection(); as selection) {
          <div
            class="pointer-events-none absolute z-10 rounded border-2 border-violet-500 bg-violet-500/20"
            [style.left.px]="getRelativeLeft(selection.combinedRect)"
            [style.top.px]="getRelativeTop(selection.combinedRect)"
            [style.width.px]="selection.combinedRect.width"
            [style.height.px]="selection.combinedRect.height"
          >
            <span
              class="absolute -top-6 left-0 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap rounded bg-violet-500 px-1.5 py-0.5 text-[10px] text-white"
            >
              {{ selection.texts.join('') }}
            </span>
          </div>
        }
      </div>

      <!-- Side Panel -->
      <div class="w-80 shrink-0 overflow-y-auto border-l border-neutral-200 bg-neutral-50">
        <!-- Layout Settings -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-400">Layout</span>
          <div class="mt-2.5 space-y-2.5">
            <div class="flex items-center justify-between">
              <span class="text-xs text-neutral-600">Highlight</span>
              <ion-toggle [checked]="localHighlight()" (ionChange)="toggleHighlight($any($event).detail.checked)" />
            </div>
            <div>
              <dojo-single-select
                [formCtrl]="alignmentControl"
                [options]="alignmentOptions"
                [optionValue]="'value'"
                [optionLabel]="'label'"
                [compact]="true"
                [borderless]="true"
              />
            </div>
          </div>
        </div>

        <!-- Anchors List -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-400">
            Anchors
            <span class="normal-case tracking-normal">({{ existingAnchors().length }})</span>
          </span>
          <div class="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto">
            @for (anchor of existingAnchors(); track anchor.id) {
              <div
                class="flex items-center justify-between rounded px-2 py-1.5 text-[11px]"
                [class.bg-emerald-50]="selectedAnchors().includes(anchor.id)"
                [class.text-emerald-700]="selectedAnchors().includes(anchor.id)"
                [class.bg-white]="!selectedAnchors().includes(anchor.id)"
              >
                <code class="font-mono text-neutral-600">{{ anchor.id }}</code>
                <button
                  class="text-neutral-300 transition-colors hover:text-red-500"
                  (click)="deleteAnchor(anchor.id)"
                  title="Delete anchor"
                >
                  <dojo-icon icon="trash" [size]="12" />
                </button>
              </div>
            }
            @if (existingAnchors().length === 0) {
              <p class="py-3 text-center text-[11px] text-neutral-400">No anchors yet</p>
            }
          </div>
        </div>

        <!-- Connections List -->
        <div class="border-b border-neutral-200 px-4 py-3">
          <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-400">
            Connections
            <span class="normal-case tracking-normal">({{ connections().length }})</span>
          </span>
          <div class="mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto">
            @for (conn of connections(); track conn.id) {
              <div
                class="flex cursor-pointer items-center gap-1.5 rounded px-2 py-1.5 text-[11px] transition-colors hover:bg-blue-50"
                [class.bg-blue-50]="selectedConnection()?.id === conn.id"
                [class.text-blue-700]="selectedConnection()?.id === conn.id"
                [class.bg-white]="selectedConnection()?.id !== conn.id"
                (click)="editConnection(conn)"
              >
                <code class="font-mono text-indigo-500">{{ conn.from_anchor }}</code>
                <dojo-icon icon="arrow-right" [size]="10" class="text-neutral-300" />
                <code class="font-mono text-indigo-500">{{ conn.to_anchor }}</code>
              </div>
            }
            @if (connections().length === 0) {
              <p class="py-3 text-center text-[11px] text-neutral-400">No connections yet</p>
            }
          </div>
        </div>

        <!-- Connection Editor -->
        @if (editingConnection(); as conn) {
          <div class="px-4 py-3">
            <span class="text-[11px] font-medium uppercase tracking-wider text-neutral-400">Edit Connection</span>

            <div class="mt-2.5 space-y-1.5">
              <ion-item class="rounded border border-neutral-200">
                <ion-select
                  label="Color"
                  labelPlacement="stacked"
                  [value]="conn.style?.color ?? 'neutral'"
                  (ionChange)="updateConnectionStyle('color', $any($event).detail.value)"
                >
                  @for (color of colorOptions; track color) {
                    <ion-select-option [value]="color">{{ color }}</ion-select-option>
                  }
                </ion-select>
              </ion-item>

              <ion-item class="rounded border border-neutral-200">
                <ion-input
                  label="Line Width"
                  labelPlacement="stacked"
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="5"
                  [value]="conn.style?.width ?? 1.2"
                  (ionInput)="updateConnectionStyle('width', +$any($event).detail.value)"
                />
              </ion-item>

              <ion-item class="rounded border border-neutral-200">
                <ion-select
                  label="Line Style"
                  labelPlacement="stacked"
                  [value]="conn.style?.line_style ?? 'solid'"
                  (ionChange)="updateConnectionStyle('line_style', $any($event).detail.value)"
                >
                  <ion-select-option value="solid">Solid</ion-select-option>
                  <ion-select-option value="dashed">Dashed</ion-select-option>
                  <ion-select-option value="dotted">Dotted</ion-select-option>
                </ion-select>
              </ion-item>

              <ion-item class="rounded border border-neutral-200">
                <ion-select
                  label="Arrow Style"
                  labelPlacement="stacked"
                  [value]="conn.style?.arrow_style ?? 'open'"
                  (ionChange)="updateConnectionStyle('arrow_style', $any($event).detail.value)"
                >
                  <ion-select-option value="none">None</ion-select-option>
                  <ion-select-option value="open">Open</ion-select-option>
                  <ion-select-option value="filled">Filled</ion-select-option>
                </ion-select>
              </ion-item>

              <div class="grid grid-cols-2 gap-1.5">
                <ion-item class="rounded border border-neutral-200">
                  <ion-select
                    label="From"
                    labelPlacement="stacked"
                    [value]="conn.style?.from_socket ?? 'Bottom'"
                    (ionChange)="updateConnectionStyle('from_socket', $any($event).detail.value)"
                  >
                    <ion-select-option value="Top">Top</ion-select-option>
                    <ion-select-option value="Bottom">Bottom</ion-select-option>
                    <ion-select-option value="Left">Left</ion-select-option>
                    <ion-select-option value="Right">Right</ion-select-option>
                  </ion-select>
                </ion-item>
                <ion-item class="rounded border border-neutral-200">
                  <ion-select
                    label="To"
                    labelPlacement="stacked"
                    [value]="conn.style?.to_socket ?? 'Top'"
                    (ionChange)="updateConnectionStyle('to_socket', $any($event).detail.value)"
                  >
                    <ion-select-option value="Top">Top</ion-select-option>
                    <ion-select-option value="Bottom">Bottom</ion-select-option>
                    <ion-select-option value="Left">Left</ion-select-option>
                    <ion-select-option value="Right">Right</ion-select-option>
                  </ion-select>
                </ion-item>
              </div>

              <ion-item class="rounded border border-neutral-200">
                <ion-input
                  label="Stub Length"
                  labelPlacement="stacked"
                  type="number"
                  min="0"
                  max="50"
                  [value]="conn.style?.stub ?? 12"
                  (ionInput)="updateConnectionStyle('stub', +$any($event).detail.value)"
                />
              </ion-item>

              <div class="flex gap-1.5 pt-1.5">
                <dojo-button [size]="'s'" [fill]="'solid'" (click)="saveConnectionEdit()" class="flex-1">
                  Save
                </dojo-button>
                <dojo-button [size]="'s'" [fill]="'outline'" (click)="cancelConnectionEdit()">Cancel</dojo-button>
                <dojo-button [size]="'s'" [fill]="'outline'" (click)="deleteConnection(conn.id)" class="text-red-500">
                  <dojo-icon icon="trash" [size]="12" />
                </dojo-button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
</div>

<!-- Anchor Creation Dialog -->
@if (showAnchorDialog()) {
  <div
    class="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40"
    (click)="closeAnchorDialog()"
    (mousedown)="$event.stopPropagation()"
    (mousemove)="$event.stopPropagation()"
    (mouseup)="$event.stopPropagation()"
  >
    <div
      class="w-80 rounded-lg border border-neutral-200 bg-white p-5 shadow-xl"
      (click)="$event.stopPropagation()"
      (mousedown)="$event.stopPropagation()"
    >
      <h3 class="mb-1 text-sm font-semibold text-neutral-900">Create Anchor</h3>
      <p class="mb-3 text-xs text-neutral-500">
        For: <strong class="text-neutral-700">"{{ pendingAnchor()?.text }}"</strong>
      </p>
      <ion-item class="mb-3 rounded border border-neutral-200">
        <ion-input
          label="Anchor ID"
          labelPlacement="stacked"
          [value]="newAnchorId()"
          (ionInput)="newAnchorId.set($any($event).detail.value)"
          placeholder="e.g., term-x, equation-1"
          (keydown.enter)="confirmCreateAnchor()"
          class="font-mono"
        />
      </ion-item>
      <div class="flex gap-2">
        <dojo-button [size]="'s'" [fill]="'solid'" (click)="confirmCreateAnchor()" class="flex-1">Create</dojo-button>
        <dojo-button [size]="'s'" [fill]="'outline'" (click)="closeAnchorDialog()">Cancel</dojo-button>
      </div>
    </div>
  </div>
}
